/// <reference path="../../types/window.d.ts" />

/**
 * Cookiebot Consent Utilities
 * Centralized consent checking for GDPR compliance across all analytics/marketing tools
 */

export type CookiebotConsentState = "granted" | "denied" | "unknown";
export type CookiebotConsentRequirement =
  | "analytics"
  | "marketing"
  | "analytics-or-marketing";
export type CookiebotConsentSnapshot = {
  cookiebot_has_response: boolean | null;
  cookiebot_consented: boolean | null;
  cookiebot_declined: boolean | null;
  cookiebot_consent_necessary: boolean | null;
  cookiebot_consent_preferences: boolean | null;
  cookiebot_consent_statistics: boolean | null;
  cookiebot_consent_marketing: boolean | null;
};
type CookiebotConsentWindow = {
  Cookiebot?: {
    consent?: {
      necessary?: boolean;
      preferences?: boolean;
      marketing?: boolean;
      statistics?: boolean;
    };
    hasResponse?: boolean;
    consented?: boolean;
    declined?: boolean;
  };
};

const getCookiebotConsentWindow = (
  targetWindow?: CookiebotConsentWindow | null,
): CookiebotConsentWindow | null => {
  // The optional window is used by GTM purchase replay paths that must read
  // consent from the same browser window that receives the replayed event.
  if (targetWindow !== undefined) {
    return targetWindow;
  }

  return typeof window === "undefined" ? null : window;
};

/**
 * Check if Cookiebot consent is given for marketing
 * Used by: Meta Pixel, Bing Ads, and other advertising/marketing pixels
 * @returns true if marketing consent is given
 */
export const isCookiebotMarketingConsentGiven = (
  targetWindow?: CookiebotConsentWindow | null,
): boolean => {
  const consentWindow = getCookiebotConsentWindow(targetWindow);
  if (!consentWindow) return false;

  const cookiebot = consentWindow.Cookiebot;
  if (!cookiebot) return false;

  return Boolean(cookiebot.consent?.marketing);
};

/**
 * Check if Cookiebot consent is given for analytics/statistics
 * Used by: Clarity, Google Analytics, and other analytics tools
 * @returns true if statistics consent is given
 */
export const isCookiebotAnalyticsConsentGiven = (
  targetWindow?: CookiebotConsentWindow | null,
): boolean => {
  const consentWindow = getCookiebotConsentWindow(targetWindow);
  if (!consentWindow) return false;

  const cookiebot = consentWindow.Cookiebot;
  if (!cookiebot) return false;

  return Boolean(cookiebot.consent?.statistics);
};

export const getCookiebotConsentSnapshot = (
  targetWindow?: CookiebotConsentWindow | null,
): CookiebotConsentSnapshot => {
  const consentWindow = getCookiebotConsentWindow(targetWindow);
  const cookiebot = consentWindow?.Cookiebot;

  return {
    cookiebot_has_response:
      cookiebot?.hasResponse === undefined ? null : Boolean(cookiebot.hasResponse),
    cookiebot_consented:
      cookiebot?.consented === undefined ? null : Boolean(cookiebot.consented),
    cookiebot_declined:
      cookiebot?.declined === undefined ? null : Boolean(cookiebot.declined),
    cookiebot_consent_necessary:
      cookiebot?.consent?.necessary === undefined
        ? null
        : Boolean(cookiebot.consent.necessary),
    cookiebot_consent_preferences:
      cookiebot?.consent?.preferences === undefined
        ? null
        : Boolean(cookiebot.consent.preferences),
    cookiebot_consent_statistics:
      cookiebot?.consent?.statistics === undefined
        ? null
        : Boolean(cookiebot.consent.statistics),
    cookiebot_consent_marketing:
      cookiebot?.consent?.marketing === undefined
        ? null
        : Boolean(cookiebot.consent.marketing),
  };
};

/**
 * Gate for writing first-touch attribution (gclid/gbraid/wbraid/UTM) into
 * first-party storage.
 *
 * Storing advertising click IDs is not "strictly necessary" under TTDSG §25 /
 * ePrivacy, so it requires an affirmative consent response before the value is
 * written to localStorage/sessionStorage. Until then the attribution lives in
 * the page-lifetime memory fallback only, which still covers the whole SPA
 * journey. Tightening this to marketing-only is a one-line change once legal
 * signs off on the exact category mapping.
 */
export const hasFirstTouchStorageConsent = (
  snapshot: CookiebotConsentSnapshot,
): boolean =>
  snapshot.cookiebot_has_response === true &&
  (snapshot.cookiebot_consent_statistics === true ||
    snapshot.cookiebot_consent_marketing === true);

/**
 * True when the visitor answered the banner without granting statistics or
 * marketing consent. Stored identifiers must be purged in that case.
 */
export const hasFirstTouchStorageWithdrawal = (
  snapshot: CookiebotConsentSnapshot,
): boolean =>
  snapshot.cookiebot_has_response === true &&
  snapshot.cookiebot_consent_statistics !== true &&
  snapshot.cookiebot_consent_marketing !== true;

export const getCookiebotConsentState = (
  requirement: CookiebotConsentRequirement,
): CookiebotConsentState => {
  if (typeof window === "undefined") {
    return "unknown";
  }

  const cookiebot = window.Cookiebot;
  if (!cookiebot) {
    return "unknown";
  }

  const hasAnalyticsConsent = Boolean(cookiebot.consent?.statistics);
  const hasMarketingConsent = Boolean(cookiebot.consent?.marketing);
  const hasRequiredConsent =
    requirement === "analytics"
      ? hasAnalyticsConsent
      : requirement === "marketing"
        ? hasMarketingConsent
        : hasAnalyticsConsent || hasMarketingConsent;

  if (hasRequiredConsent) {
    return "granted";
  }

  // Cookiebot exposes hasResponse/consented/declined for known banner states.
  // consented can be true for partial accepts, so this only means the current
  // requirement was not granted.
  if (cookiebot.hasResponse || cookiebot.consented || cookiebot.declined) {
    return "denied";
  }

  return "unknown";
};
